import type { ReleaseManifest } from "@m8t-stack/platform-release";
import { compareSemver } from "./platform-update.js";
import { runAz } from "./az.js";

/** Normalize a version string to the vX.Y.Z form compareSemver expects. */
function toVTag(v: string): string {
  return v.startsWith("v") ? v : `v${v}`;
}

/**
 * Should the updater job self-update its OWN image before applying the target?
 *
 * Two triggers, either fires:
 *  - PRIMARY (running image ≠ the manifest's installer image): the job applies
 *    release content BAKED into its image (`/opt/m8t/repo`), not a per-commit
 *    fetch, so an engine running a different image than the target pins carries
 *    the wrong persona/infra content for it. See needsEngineImageHop for why
 *    this compares images rather than versions.
 *  - SECONDARY (cli.min): the baked CLI engine is below the manifest's minimum
 *    supported CLI — the pre-existing D7 compat hop.
 *
 * The dev sentinel (`0.0.0-dev`, tsx) never self-updates. With no running image
 * to compare (founder/dev path), only the cli.min trigger applies.
 */
export function needsEngineUpdate(manifest: ReleaseManifest, runningVersion: string, runningImage?: string | null): boolean {
  if (runningVersion === "0.0.0-dev") return false; // dev/tsx never self-updates
  if (needsEngineImageHop(manifest, runningVersion, runningImage ?? null)) return true;
  const running = toVTag(runningVersion);
  const min = toVTag(manifest.components.cli.min);
  return compareSemver(running, min) < 0;
}

/**
 * Should the engine replace its OWN image BEFORE trying to claim?
 *
 * This exists because of a deadlock that actually happened. The self-update hop
 * used to run only after a successful claim — so an engine carrying a bug in the
 * claim path could never reach the newer image that fixed it. A canary install
 * sat wedged for twelve days with a healthy-looking updater ticking every five
 * minutes: its engine predated its own lease-rescue fix, could not claim the
 * abandoned row, and therefore never hopped. The repair had shipped and was
 * structurally undeliverable.
 *
 * Any future defect in the claim path has the same shape, so the hop has to be
 * reachable without a claim.
 *
 * PRIMARY TRIGGER ONLY. The cli.min trigger deliberately stays behind the claim
 * (see needsEngineUpdate): it cannot cause this deadlock, because being too old
 * to APPLY never stops an engine from CLAIMING, and hopping on it here could
 * loop when a manifest pins an installer whose own baked CLI sits below that
 * floor.
 */
export function needsPreClaimEngineUpdate(
  manifest: ReleaseManifest,
  runningVersion: string,
  runningImage?: string | null,
): boolean {
  return needsEngineImageHop(manifest, runningVersion, runningImage ?? null);
}

/** The engine image a manifest pins. The one string both the hop decision and
 *  the hop itself are written against, so they cannot disagree. */
export function targetEngineImage(manifest: ReleaseManifest): string {
  const inst = manifest.components.installer;
  return `${inst.ref}:${inst.tag}`;
}

/** The updater job's own name, from the environment bicep sets on it
 *  (deploy/modules/updater-job.bicep). Absent ⇒ not running AS the job — a
 *  founder or dev invocation — which is a legitimate state, not an error. */
export function ownJobName(env: NodeJS.ProcessEnv = process.env): string | null {
  const name = (env.UPDATER_JOB_NAME ?? "").trim();
  return name.length > 0 ? name : null;
}

/**
 * The image the updater job is running RIGHT NOW.
 *
 * Returns null when there is no job to ask about (founder/dev path). Throws if
 * the job is named but unreadable: a hop decision made without knowing what is
 * running is a guess, and guessing "no hop" would converge from whatever stale
 * content the current image happens to carry.
 */
export async function readOwnJobImage(opts: {
  subscriptionId: string;
  resourceGroup: string;
  jobName?: string | null;
}): Promise<string | null> {
  const job = opts.jobName ?? ownJobName();
  if (!job) return null;
  const image = (await runAz([
    "containerapp", "job", "show",
    "-n", job,
    "-g", opts.resourceGroup,
    "--subscription", opts.subscriptionId,
    "--query", "properties.template.containers[0].image",
    "-o", "tsv",
    "--only-show-errors",
  ])).trim();
  if (!image || image === "None") {
    throw new Error(`could not read the updater job's current image (job ${job}) — refusing to decide a self-update blind`);
  }
  return image;
}

/**
 * Should the engine replace its own image? Compares IMAGES, not versions.
 *
 * This used to compare the manifest's installer VERSION against the version
 * baked into the running image, and that worked only because on the stable rail
 * `installer/version.json` bumps whenever installer content changes. On the
 * canary rail it does not: a canary changes installer CONTENT — producing a new
 * content-addressed tag — while the version file stays put. So a version
 * comparison answers "equal" forever, the engine never hops, and every canary
 * converges from an older image's baked deploy/, personas/ and CLI while
 * reporting success. Loud drift replaced by silent drift.
 *
 * Comparing the image is comparing the thing that actually moves. It is also
 * what the version comparison was always a proxy for — "does my baked content
 * match the target's" — asked directly instead of through a stand-in that only
 * happened to correlate.
 *
 * Terminating by construction, and more obviously than before: after a hop the
 * running image IS `targetEngineImage(manifest)`, so the next tick compares
 * equal. There is no arithmetic to get wrong.
 *
 * `runningImage === null` means this is not the updater job (founder/dev), which
 * has no own image to replace: false.
 *
 * THE COMPARISON IS EXACT-STRING, AND ITS FAILURE IS BOUNDED. If a job is
 * running an equivalent-but-differently-spelled ref — an operator passing a
 * digest pin to `deploy --installer-image`, say — the first comparison is
 * unequal and a hop fires. But the hop WRITES `targetEngineImage(manifest)`, so
 * the next tick compares equal. It costs exactly one redundant hop and cannot
 * loop, which is the property that makes exact-string comparison safe enough to
 * prefer over normalisation nobody would be able to keep correct.
 */
export function needsEngineImageHop(
  manifest: ReleaseManifest,
  runningVersion: string,
  runningImage: string | null,
): boolean {
  if (runningVersion === "0.0.0-dev") return false; // dev/tsx never self-updates
  if (runningImage === null || runningImage.trim().length === 0) return false;
  return runningImage.trim() !== targetEngineImage(manifest);
}

/** First-step escape hatch: bump the updater job's OWN image to the target installer, then exit;
 *  the next cron tick re-executes with the new engine. Uses the installer component pin. */
export async function updateOwnJobImage(opts: {
  subscriptionId: string;
  resourceGroup: string;
  manifest: ReleaseManifest;
  jobName?: string;
}): Promise<void> {
  // Through targetEngineImage, NOT a second expression that happens to match.
  // The whole termination argument is "after a hop the running image IS
  // targetEngineImage(manifest), so the next tick compares equal" — and that
  // holds only while the writer and the reader produce byte-identical strings.
  // Two independent copies make it true by coincidence; anyone normalising one
  // of them (casing, a digest, a registry prefix) would make the decision and
  // the hop disagree, and the comparison is exact-string, so a cosmetic
  // difference is enough. The failure would be hop-every-tick, never claim:
  // exactly the bug this file was rewritten to remove.
  const image = targetEngineImage(opts.manifest);
  const job = opts.jobName ?? ownJobName();
  if (!job) throw new Error("UPDATER_JOB_NAME unset — cannot self-update the updater job image");
  await runAz([
    "containerapp",
    "job",
    "update",
    "-n",
    job,
    "-g",
    opts.resourceGroup,
    "--subscription",
    opts.subscriptionId,
    "--image",
    image,
    "--only-show-errors",
  ]);
}
